import React, { useState } from 'react'
import { css } from '@emotion/core'
import { Button, List, ListItem, InlineButton } from '@fs/zion-ui'
import { useTranslation, Trans } from 'react-i18next'
import { MenuNewWindow, DocumentBasic } from '@fs/zion-icon'
import { getNestedOrFloatingElementById, getPersonName, openRelationshipArk, useSlsStateContext } from '@fs/zion-sls'

export function RelationshipListSection({ relationships, useIconComponent = false }) {
  const state = useSlsStateContext()
  const [t] = useTranslation()
  const IconComponent = useIconComponent ? ListItem.Icon : ListItem.IconButton

  return (
    <List>
      {relationships.map((relationship) => {
        const relationshipPerson = getNestedOrFloatingElementById(state.images, relationship.otherPersonId)
        return (
          <ListItem
            key={relationship.id}
            overline={`${
              relationshipPerson?.primary
                ? `${t('edit-relationships.choose-record-overlay.principal', 'Principal')} | `
                : ''
            }${relationship.label}`}
            primaryText={relationship.value}
            startElement={<ListItem.Icon Icon={DocumentBasic} />}
            endElement={
              <IconComponent
                Icon={MenuNewWindow}
                onClick={() => {
                  openRelationshipArk(relationshipPerson, relationship, state.currentImageArk)
                }}
              />
            }
          />
        )
      })}
    </List>
  )
}

export default function ChooseRecordOverlayStepTwo({
  personBeingMoved,
  personStaying,
  record1Relationships,
  record2Relationships,
  selectedRecord,
}) {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const [seeMore, setSeeMore] = useState(false)
  const name = getPersonName({ person: personBeingMoved })
  const otherName = getPersonName({ person: personStaying })
  const existingRelationships = selectedRecord === 'current' ? record2Relationships : record1Relationships
  const relationshipsInDestRecord = selectedRecord === 'current' ? record1Relationships : record2Relationships
  const relationshipsListCss = css`
    max-height: 140px;
    overflow-x: hidden;
    overflow-y: ${seeMore ? 'scroll' : 'hidden'};
  `
  return (
    <>
      {existingRelationships.length === 0 && (
        <div>
          <Trans i18nKey="edit-relationships.choose-record-overlay.move-person">
            Move <i>{{ name }}</i> to
            <strong> Record {{ recordNum: selectedRecord === 'current' ? '1' : '2' }}</strong>
          </Trans>
          <List>
            <ListItem primaryText={name} startElement={<ListItem.Icon Icon={DocumentBasic} />} />
            <ListItem primaryText={otherName} startElement={<ListItem.Icon Icon={DocumentBasic} />} />
          </List>
          {relationshipsInDestRecord.length > 0 && (
            <>
              {t('edit-relationships.choose-record-overlay.relationships-of', 'Relationships of {name}:', {
                name: otherName,
              })}
            </>
          )}
          <List>
            {relationshipsInDestRecord.map((relationship) => {
              const relationshipPerson = getNestedOrFloatingElementById(state.images, relationship.otherPersonId)
              return (
                <ListItem
                  key={relationship.id}
                  overline={`${
                    relationshipPerson?.primary
                      ? `${t('edit-relationships.choose-record-overlay.principal', 'Principal')} | `
                      : ''
                  }${relationship.label}`}
                  primaryText={relationship.value}
                  startElement={<ListItem.Icon Icon={DocumentBasic} />}
                  endElement={
                    <ListItem.Icon
                      Icon={MenuNewWindow}
                      onClick={() => {
                        openRelationshipArk(relationshipPerson, relationship, state.currentImageArk)
                      }}
                    />
                  }
                />
              )
            })}
          </List>
        </div>
      )}
      {existingRelationships.length > 0 && (
        <div>
          <Trans i18nKey="edit-relationships.choose-record-overlay.approve-instructions">
            The old relationships of <i>{{ name }}</i> will need to be removed.
            <strong> Approve</strong> removal of relationships below.
          </Trans>
          &nbsp;
          <InlineButton to="https://www.familysearch.org/help/helpcenter/article/how-do-i-add-a-name-to-a-relationship-in-historical-records-when-the-name-is-currently-connected-to-the-wrong-record">
            {t('edit-relationships.choose-record-overlay.learn-more', 'Learn more')}
          </InlineButton>
          <List>
            <ListItem
              primaryText={name}
              overline={
                personBeingMoved?.primary ? t('edit-relationships.choose-record-overlay.principal', 'Principal') : ''
              }
              startElement={<ListItem.Icon Icon={DocumentBasic} />}
            />
          </List>
          {t(
            'edit-relationships.choose-record-overlay.view-relationships',
            'The following relationships will be removed:'
          )}
          <div css={relationshipsListCss}>
            <RelationshipListSection relationships={existingRelationships} useIconComponent />
          </div>
        </div>
      )}
      {existingRelationships.length > 2 ? (
        <Button onClick={() => setSeeMore(!seeMore)}>
          {t('edit-relationships.choose-record-overlay.see-more.button', 'See More')}
        </Button>
      ) : null}
    </>
  )
}
